import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, orderBy, doc, deleteDoc, getDocs, getDoc, updateDoc, addDoc, arrayUnion, writeBatch, collectionGroup, where, limit, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import Suggestions from '../components/Suggestions';
import { useNavigate } from 'react-router-dom';
import VerseSelectionWidget from '../components/VerseSelectionWidget';
import GrowthAreaPieChart from '../components/GrowthAreaPieChart';
import { Cog6ToothIcon, DocumentTextIcon, BookOpenIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import DiscipleSettings from '../components/DiscipleSettings';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import MissionalScale from '../components/MissionalScale';
import SuggestedVerses from '../components/SuggestedVerses';
import SearchVerses from '../components/SearchVerses';
import MentoringNotesSection from '../components/MentoringNotesSection';
import { ClockIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import SentVerses from '../components/SentVerses';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { initializeContentTrackProgress } from '../utils/contentTrackProgress';

const initializeReminderSettings = async (discipleId, userAppID) => {
  const batch = writeBatch(db);
  const now = new Date();
  
  // Get the disciple document to access createdAt
  const discipleRef = doc(db, 'disciples', discipleId);
  const discipleDoc = await getDoc(discipleRef);
  const createdAt = discipleDoc.data()?.createdAt || now;
  
  // Get the mentor details document
  const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
  
  // Set default reminder settings in one batch write
  batch.set(mentorDetailsRef, {
    reminderSettings: {
      enabled: true,
      frequency: 7, // Default - user can change to any number 1-31
      preferredTime: '09:00',
      lastInteractionDate: createdAt.toISOString(), // Default to creation date
      lastMessageDate: createdAt.toISOString(), // Default to creation date
      nextReminderDate: new Date(createdAt.getTime() + (7 * 24 * 60 * 60 * 1000)).toISOString(), // 7 days from creation
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      notificationMethod: 'fcm',
      quietHours: {
        start: "22:00",
        end: "08:00"
      }
    }
  }, { merge: true });

  await batch.commit();
  console.log('🟢 Initialized reminder settings with creation date:', createdAt);
};

const createAutomaticReminder = async (discipleId, discipleName, nextReminderDate, userAppID) => {
  try {
    const reminderQueueRef = collection(db, 'users', userAppID, 'reminderQueue');
    
    // Create the reminder
    await addDoc(reminderQueueRef, {
      discipleId,
      discipleName,
      mentorId: userAppID,
      message: `Time to connect with ${discipleName}`,
      nextReminderDate: nextReminderDate.toISOString(),
      createdAt: serverTimestamp(),
      queueStatus: 'queued',
      reminderStatus: 'scheduled',
      archived: false,
      notificationMethod: 'fcm',
      messageSent: false,
      notificationsSent: 0,
      attempts: 0
    });

    console.log('🟢 Created automatic reminder for:', discipleName, 'next reminder:', nextReminderDate);
  } catch (error) {
    console.error('Error creating automatic reminder:', error);
  }
};

const ViewDisciple = () => {
  const { discipleId } = useParams();
  const { currentUser } = useAuth();
  const [discipleData, setDiscipleData] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userAppID, setUserAppID] = useState('');
  const [updatedData, setUpdatedData] = useState({
    email: '',
    phone: '',
    photo: '',
    reminderSettings: {
      enabled: true,
      frequency: 7,
      preferredTime: '09:00',
      lastMessageDate: null,
      nextReminderDate: null
    }
  });
  const [showUpdateProfile, setShowUpdateProfile] = useState(false);
  const [suggestedNextVerses, setSuggestedNextVerses] = useState([]);
  const [showVerseSelector, setShowVerseSelector] = useState(false);
  const [allVerses, setAllVerses] = useState([]);
  const [userTimeZone, setUserTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showSettings, setShowSettings] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [photoURL, setPhotoURL] = useState(mentorDetails?.photoURL || null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [messageRefreshTrigger, setMessageRefreshTrigger] = useState(0);
  const [sentVerses, setSentVerses] = useState([]);

  // Survey module (Listen): surveys list, current survey + questions, next 4
  const [surveys, setSurveys] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [surveyUpdating, setSurveyUpdating] = useState(false);
  const [showAllSurveysModal, setShowAllSurveysModal] = useState(false);

  const SURVEYS_SHOWN_INLINE = 5;

  // Content module (Help): current track name, next 3 resources; other tracks (first 5 never subscribed)
  const [contentTrack, setContentTrack] = useState(null);
  const [nextContentResources, setNextContentResources] = useState([]);
  const [otherContentTracks, setOtherContentTracks] = useState([]);
  const [contentTrackSubscribing, setContentTrackSubscribing] = useState(null);

  const mentorRef = useCallback(() => doc(db, 'disciples', discipleId, 'mentorDetails', userAppID), [discipleId, userAppID]);
  const subscribedSurveyId = mentorDetails?.subscribedSurveyId ?? null;
  const surveyProgress = mentorDetails?.surveyProgress ?? { currentQuestionIndex: 0, step: 'listen' };
  const currentQuestionIndex = surveyProgress.currentQuestionIndex ?? 0;
  const subscribedContentTrackId = mentorDetails?.subscribedContentTrackId ?? null;
  const contentTrackProgress = mentorDetails?.contentTrackProgress ?? null;

  const fetchAllVerses = async () => {
    try {
      const versesSnapshot = await getDocs(collection(db, 'verses'));
      const verses = versesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllVerses(verses); // Store fetched verses in state
    } catch (error) {
      console.error('Error fetching all verses:', error);
    }
  };
  
  // Fetch verses on component mount
  useEffect(() => {
    fetchAllVerses();
  }, []);
  

  useEffect(() => {
    if (discipleId && currentUser) {
      fetchUserAppID();
    }
  }, [discipleId, currentUser]);

  useEffect(() => {
    const fetchData = async () => {
      if (!discipleId || !userAppID) return;

      try {
        const [discipleDoc, mentorDetailsDoc] = await Promise.all([
          getDoc(doc(db, 'disciples', discipleId)),
          getDoc(doc(db, 'disciples', discipleId, 'mentorDetails', userAppID))
        ]);

        if (discipleDoc.exists()) {
          const data = discipleDoc.data();
          setDiscipleData({
            ...data,
            birthday: data.birthday || '',
          });
        }

        if (mentorDetailsDoc.exists()) {
          const data = mentorDetailsDoc.data();
          // Only initialize if reminderSettings doesn't exist
          if (!data.reminderSettings) {
            await initializeReminderSettings(discipleId, userAppID);
          }
          setMentorDetails(data);
        } else {
          // Initialize new mentor details if document doesn't exist
          await initializeReminderSettings(discipleId, userAppID);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [discipleId, userAppID, refreshTrigger]);

  useEffect(() => {
    if (discipleData) {
      setUpdatedData({
        email: discipleData.email || '',
        phone: discipleData.phone || '',
        photo: discipleData.photoURL || '',
        reminderSettings: {
          enabled: discipleData.reminderSettings?.enabled ?? true,
          frequency: discipleData.reminderSettings?.frequency || 7,
          preferredTime: discipleData.reminderSettings?.preferredTime || '09:00',
          lastMessageDate: discipleData.reminderSettings?.lastMessageDate || null,
          nextReminderDate: discipleData.reminderSettings?.nextReminderDate || null
        }
      });
    }
  }, [discipleData]);

  const fetchUserAppID = async () => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        setUserAppID(userDoc.data().appID); // Assuming `setUserAppID` is a state setter in ViewDisciple.js
      } else {
        console.error('User document does not exist');
      }
    } catch (error) {
      console.error('Error fetching userAppID:', error);
    }
  };
  useEffect(() => {
    if (currentUser) {
      fetchUserAppID();
    }
  }, [currentUser]);
  
  console.log('Fetching userAppID for:', currentUser?.uid);

  const fetchDiscipleData = async (discipleId) => {
    try {
      const discipleDocRef = doc(db, 'disciples', discipleId);
      const discipleDoc = await getDoc(discipleDocRef);
  
      if (discipleDoc.exists()) {
        const data = discipleDoc.data();
        setDiscipleData(data);
        setUpdatedData((prev) => ({
          ...prev,
          email: data.email || prev.email,
          phone: data.phone || prev.phone,
        }));
      } else {
        console.error('No disciple found with the provided discipleId.');
      }
    } catch (error) {
      console.error('Error fetching disciple data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  

  useEffect(() => {
    const fetchMentorDetails = async () => {
      if (!discipleId || !userAppID) return;
      
      try {
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
        const mentorDetailsSnap = await getDoc(mentorDetailsRef);
        
        if (mentorDetailsSnap.exists()) {
          const data = mentorDetailsSnap.data();
          setMentorDetails(data);
        }
      } catch (error) {
        console.error('Error fetching mentor details:', error);
      }
    };

    fetchMentorDetails();
  }, [discipleId, userAppID]); // Only run when these values change

  // Load surveys list (for Survey module subscribe picker)
  useEffect(() => {
    if (!userAppID) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'surveys'));
        if (!cancelled) setSurveys(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        if (!cancelled) console.error('Error loading surveys:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [userAppID]);

  const loadSurveyAndQuestions = useCallback(async (surveyIdParam) => {
    const id = surveyIdParam ?? subscribedSurveyId;
    if (!id) return;
    try {
      const surveySnap = await getDoc(doc(db, 'surveys', id));
      if (surveySnap.exists()) setSurvey({ id: surveySnap.id, ...surveySnap.data() });
      const qRef = collection(db, 'surveys', id, 'questions');
      let snap;
      try {
        snap = await getDocs(query(qRef, orderBy('order', 'asc')));
      } catch {
        snap = await getDocs(qRef);
      }
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSurveyQuestions(list);
    } catch (err) {
      console.error('Error loading survey questions:', err);
    }
  }, [subscribedSurveyId]);

  useEffect(() => {
    if (subscribedSurveyId) loadSurveyAndQuestions(subscribedSurveyId);
    else {
      setSurvey(null);
      setSurveyQuestions([]);
    }
  }, [subscribedSurveyId, loadSurveyAndQuestions]);

  const subscribeToSurvey = async (surveyId) => {
    if (!userAppID) return;
    setSurveyUpdating(true);
    try {
      await setDoc(mentorRef(), {
        subscribedSurveyId: surveyId,
        surveyProgress: { currentQuestionIndex: 0, step: 'listen' },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      const s = surveys.find(x => x.id === surveyId);
      if (s) setSurvey(s);
      await loadSurveyAndQuestions(surveyId);
      setMentorDetails(prev => ({
        ...prev,
        subscribedSurveyId: surveyId,
        surveyProgress: { currentQuestionIndex: 0, step: 'listen' },
      }));
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error subscribing to survey:', err);
    } finally {
      setSurveyUpdating(false);
    }
  };

  const unsubscribeFromSurvey = async () => {
    if (!window.confirm('Unsubscribe from this survey? Progress will be lost.')) return;
    setSurveyUpdating(true);
    try {
      await updateDoc(mentorRef(), {
        subscribedSurveyId: null,
        surveyProgress: null,
        updatedAt: serverTimestamp(),
      });
      setMentorDetails(prev => ({ ...prev, subscribedSurveyId: null, surveyProgress: null }));
      setSurvey(null);
      setSurveyQuestions([]);
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error unsubscribing:', err);
    } finally {
      setSurveyUpdating(false);
    }
  };

  // Content module: load track name and next 4 resources
  useEffect(() => {
    if (!subscribedContentTrackId) {
      setContentTrack(null);
      setNextContentResources([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [trackSnap, resourcesSnap] = await Promise.all([
          getDoc(doc(db, 'contentTracks', subscribedContentTrackId)),
          getDocs(collection(db, 'contentTracks', subscribedContentTrackId, 'resources')),
        ]);
        if (cancelled) return;
        const trackData = trackSnap.exists() ? { id: trackSnap.id, ...trackSnap.data() } : null;
        setContentTrack(trackData);
        const sentIds = contentTrackProgress?.sentResourceIds || [];
        const resources = resourcesSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r => r.status === 'published' || !r.status)
          .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
        const next = resources.filter(r => !sentIds.includes(r.id)).slice(0, 3);
        setNextContentResources(next);
      } catch (err) {
        if (!cancelled) console.error('Error loading content track:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [subscribedContentTrackId, contentTrackProgress?.sentCount]);

  // Load first 5 content tracks this disciple has never been subscribed to (exclude current)
  useEffect(() => {
    if (!discipleId || !userAppID) return;
    let cancelled = false;
    const isChild = discipleData?.isChild === true;
    (async () => {
      try {
        const publishedQuery = query(collection(db, 'contentTracks'), where('status', '==', 'published'));
        const activeQuery = query(collection(db, 'contentTracks'), where('status', '==', 'active'));
        const publicQuery = query(collection(db, 'contentTracks'), where('visibility.isPublic', '==', true));
        const [publishedSnap, activeSnap, publicSnap] = await Promise.all([
          getDocs(publishedQuery),
          getDocs(activeQuery),
          getDocs(publicQuery),
        ]);
        const allDocs = [...publishedSnap.docs, ...activeSnap.docs, ...publicSnap.docs];
        const unique = allDocs.filter((d, i, self) => self.findIndex(x => x.id === d.id) === i);
        let list = unique.map(d => ({ id: d.id, ...d.data() }));
        if (isChild) list = list.filter(t => t.trackType === 'children');
        else list = list.filter(t => t.trackType === 'standard' || t.trackType === undefined);
        const excludeId = mentorDetails?.subscribedContentTrackId ?? null;
        const other = list.filter(t => t.id !== excludeId).slice(0, 5);
        if (!cancelled) setOtherContentTracks(other);
      } catch (err) {
        if (!cancelled) console.error('Error loading other content tracks:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [discipleId, userAppID, discipleData?.isChild, mentorDetails?.subscribedContentTrackId]);

  const subscribeToContentTrack = async (trackId) => {
    if (!discipleId || !userAppID) return;
    try {
      setContentTrackSubscribing(trackId);
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const progress = await initializeContentTrackProgress(trackId, discipleId, userAppID);
      await setDoc(mentorDetailsRef, {
        subscribedContentTrackId: trackId,
        contentTrackProgress: progress,
      }, { merge: true });
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error subscribing to content track:', err);
    } finally {
      setContentTrackSubscribing(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const sanitizeReminderSettings = (settings = {}) => {
    return {
      enabled: settings.enabled ?? true,
      frequency: parseInt(settings.frequency || 7),
      preferredTime: settings.preferredTime || '09:00',
      timeZone: userTimeZone,
      lastMessageDate: settings.lastMessageDate || null,
      nextReminderDate: settings.nextReminderDate || null
    };
  };

  const handleSaveDiscipleData = async () => {
    if (!userAppID || !discipleId) return;

    try {
      // Update only the basic disciple data first
      const discipleDocRef = doc(db, 'disciples', discipleId);
      
      // Clean and format the phone number - keep only digits
      const cleanPhone = updatedData.phone ? updatedData.phone.replace(/\D/g, '') : '';
      
      console.log('Updating disciple with phone:', cleanPhone); // Debug log

      // Only update what we need
      await updateDoc(discipleDocRef, {
        phone: cleanPhone,
        email: updatedData.email || ''
      });

      // Update photo URL separately if needed
      if (updatedData.photo) {
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
        await updateDoc(mentorDetailsRef, {
          photoURL: updatedData.photo
        });
      }

      console.log('✅ Basic data updated successfully');
      
      // Refresh the page data
      fetchDiscipleData();
      setShowUpdateProfile(false);

    } catch (error) {
      console.error('❌ Error updating data:', error);
      alert(`Failed to save changes: ${error.message}`);
    }
  };
  
  
  

  const handleVerseSelect = async (verse) => {
    try {
      console.log('Selected verse:', verse);
      
      // Make sure we're passing all required data in the correct structure
      navigate(`/send-message`, {
        state: { 
          verse: {
            reference: verse.reference,
            text: verse.text,
            question: verse.reflectionQuestion || verse.question, // Handle both possible field names
            growthArea: verse.growthArea,
            tags: verse.tags
          },
          discipleId: discipleId,
          discipleName: discipleData?.name || '',
          userAppID: userAppID,
          // Add any other required fields for the send message page
        }
      });
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  // Function to calculate next reminder date based on frequency and last message
  const calculateNextReminderDate = (lastMessageDate, frequency, preferredTime = '09:00') => {
    const baseDate = new Date(lastMessageDate);
    const [hours, minutes] = preferredTime.split(':');
    
    // Set the preferred time
    baseDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // Add frequency days
    baseDate.setDate(baseDate.getDate() + parseInt(frequency));
    
    // If the calculated date is in the past, keep adding frequency days until it's in the future
    const now = new Date();
    while (baseDate <= now) {
      baseDate.setDate(baseDate.getDate() + parseInt(frequency));
    }
    
    return baseDate;
  };

  // Add this function to check and initialize reminder settings
  const initializeReminderSettings = async () => {
    if (!userAppID || !discipleId) return;

    try {
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const mentorDoc = await getDoc(mentorDetailsRef);

      if (mentorDoc.exists()) {
        const data = mentorDoc.data();
        
        // Check if reminderSettings exists and has all required fields
        if (!data.reminderSettings || 
            !data.reminderSettings.hasOwnProperty('enabled') ||
            !data.reminderSettings.hasOwnProperty('frequency') ||
            !data.reminderSettings.hasOwnProperty('preferredTime')) {
          
          // Initialize with default values
          await updateDoc(mentorDetailsRef, {
            reminderSettings: {
              enabled: true,
              frequency: 7,
              preferredTime: '09:00',
              lastMessageDate: null,
              nextReminderDate: null,
              ...data.reminderSettings // Preserve any existing settings
            }
          });
          
          console.log('Reminder settings initialized successfully');
        }
      }
    } catch (error) {
      console.error('Error initializing reminder settings:', error);
    }
  };

  // Call this in useEffect when component mounts
  useEffect(() => {
    if (discipleData) {
      initializeReminderSettings();
    }
  }, [discipleData]); // Add other dependencies as needed

  const formatLocalTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
    return formatInTimeZone(date, userTimeZone, 'HH:mm');
  };

  // Add a new state to track note updates
  const [noteUpdateTrigger, setNoteUpdateTrigger] = useState(0);

  const [showSearch, setShowSearch] = useState(false);

  const [discipleNotes, setDiscipleNotes] = useState([]);

  const fetchDiscipleNotes = async () => {
    if (!discipleId || !userAppID) return;
    
    try {
      const notesRef = collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'notes');
      const notesSnapshot = await getDocs(notesRef);
      
      const notesList = notesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          note: doc.data().note,
          createdAt: doc.data().createdAt,
          ...doc.data()
        }))
        .filter(note => !note.archived) // Only non-archived notes
        .sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds); // Most recent first

      console.log('Fetched notes:', notesList);
      setDiscipleNotes(notesList);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  useEffect(() => {
    if (discipleId && userAppID) {
      fetchDiscipleNotes();
    }
  }, [discipleId, userAppID]);

  const handleSuggestionsGenerated = useCallback((newSuggestions) => {
    setSuggestedNextVerses(newSuggestions);
  }, []); // Empty dependency array since it doesn't depend on any props/state

  useEffect(() => {
    const fetchActiveReminders = async () => {
      if (!discipleId || !currentUser) return;
      
      try {
        const reminderQueueRef = collection(db, 'users', currentUser.uid, 'reminderQueue');
        const activeQuery = query(
          reminderQueueRef,
          where('discipleId', '==', discipleId),
          where('queueStatus', 'in', ['sent', 'pending']),
          where('reminderStatus', 'in', ['unread', 'wait', 'inProgress'])
        );
        
        const snapshot = await getDocs(activeQuery);
        const reminders = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('🔔 ViewDisciple - Active Reminders:', reminders.map(reminder => ({
          id: reminder.id,
          status: reminder.reminderStatus,
          discipleName: reminder.discipleName,
          scheduledFor: reminder.scheduledFor?.toDate(),
        })));
        
      } catch (error) {
        console.error('Error fetching active reminders:', error);
      }
    };

    fetchActiveReminders();
  }, [discipleId, currentUser]);

  useEffect(() => {
    if (discipleData && userAppID) {
      // Only log once when data is initially loaded
      console.log('Initial data loaded');
    }
  }, [discipleData, userAppID]); // Only run once when these values are first set

  const updateDiscipleMissionalScale = async (value) => {
    if (!discipleId || !userAppID) {
      console.error('Missing required props:', { discipleId, userAppID });
      return;
    }

    try {
      // Update in the correct location: disciples/discipleId/mentorDetails/userAppID
      const mentorDetailsRef = doc(
        db, 
        'disciples', 
        discipleId, 
        'mentorDetails', 
        userAppID
      );

      await setDoc(mentorDetailsRef, {
        missionalScale: value,
        lastUpdated: serverTimestamp()
      }, { merge: true });

    } catch (error) {
      console.error('Error updating missional scale:', error);
    }
  };

  const handleNoteAdded = useCallback(async () => {
    console.log('🟡 ViewDisciple - Note added, reloading notes and suggestions');
    
    // First, reload the notes
    try {
      const mentorNotesRef = collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'notes');
      const notesSnapshot = await getDocs(mentorNotesRef);
      const updatedNotes = notesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Update the notes state
      setDiscipleNotes(updatedNotes);
      
      // Clear current suggestions and trigger refresh
      setSuggestedNextVerses([]);
      setNoteUpdateTrigger(prev => prev + 1);
      
      console.log('🟡 ViewDisciple - Notes reloaded, triggering suggestions refresh');
    } catch (error) {
      console.error('Error reloading notes:', error);
    }
  }, [discipleId, userAppID]);

  // Update the handleReminderSettingChange function
  const handleReminderSettingChange = async (field, value) => {
    try {
      const mentorRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
      const currentData = { ...updatedData.reminderSettings };
      
      if (field === 'frequency' || field === 'preferredTime') {
        // Calculate next reminder date based on last message or current time
        const lastMessageDate = currentData.lastMessageDate || new Date().toISOString();
        const nextDate = calculateNextReminderDate(
          lastMessageDate,
          field === 'frequency' ? value : currentData.frequency,
          field === 'preferredTime' ? value : currentData.preferredTime
        );
        
        currentData.nextReminderDate = nextDate.toISOString();
        currentData[field] = value;
        
        // Create new automatic reminder
        await createAutomaticReminder(
          discipleId,
          discipleData.name,
          nextDate,
          userAppID
        );
      } else {
        currentData[field] = value;
      }

      // Update mentor details with new settings
      await updateDoc(mentorRef, {
        reminderSettings: currentData
      });

      setUpdatedData((prev) => ({
        ...prev,
        reminderSettings: currentData
      }));
    } catch (error) {
      console.error('Error updating reminder settings:', error);
    }
  };

  const handleSentVersesUpdate = useCallback((sentVerses) => {
    setSentVerses(sentVerses);
  }, []);

  if (loading) {
    return <p className="text-center mt-8 text-gray-500">Loading disciple information...</p>;
  }

  if (!discipleData) {
    return <p className="text-center mt-8 text-red-500">Disciple not found.</p>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Mobile Header (< 768px) */}
            <div className="md:hidden">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="relative h-12 w-12">
                    {mentorDetails?.photoURL ? (
                      <img
                        src={mentorDetails.photoURL}
                        alt={discipleData?.name || 'Disciple'}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserCircleIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Discipleship Planning for {discipleData?.name?.split(/\s+/)[0] || '...'}</h1>
                    <p className="text-sm text-gray-600 mt-0.5">Your notebook to make content selections, take notes, and choose next questions for your Conversations.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link
                    to={`/view-disciple/${discipleId}/listen-connect-help`}
                    className="p-2 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50"
                    title="Listen, Connect & Help"
                  >
                    <ChatBubbleBottomCenterTextIcon className="h-6 w-6" />
                  </Link>
                  <Link
                    to={`/disciple-menu/${discipleId}`}
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                    title="Menu"
                  >
                    <Squares2X2Icon className="h-6 w-6" />
                  </Link>
                  <Link
                    to={`/disciple-history/${discipleId}`}
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                    title="View History"
                  >
                    <ClockIcon className="h-6 w-6" />
                  </Link>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                  >
                    <Cog6ToothIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Tablet Portrait View (768px - 1023px) */}
            <div className="hidden md:block lg:hidden">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div className="relative h-14 w-14">
                    {mentorDetails?.photoURL ? (
                      <img
                        src={mentorDetails.photoURL}
                        alt={discipleData?.name || 'Disciple'}
                        className="h-14 w-14 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserCircleIcon className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Discipleship Planning for {discipleData?.name?.split(/\s+/)[0] || '...'}</h1>
                    <p className="text-sm text-gray-600 mt-0.5">Your notebook to make content selections, take notes, and choose next questions for your Conversations.</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <Link
                    to={`/view-disciple/${discipleId}/listen-connect-help`}
                    className="px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-md text-sm text-indigo-800 hover:bg-indigo-100 inline-flex items-center gap-2"
                  >
                    <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />
                    Listen, Connect & Help
                  </Link>
                  <Link
                    to={`/disciple-menu/${discipleId}`}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Menu
                  </Link>
                  <Link
                    to={`/disciple-history/${discipleId}`}
                    state={{ discipleData, userAppID }}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700"
                  >
                    Message History
                  </Link>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200"
                  >
                    <Cog6ToothIcon className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop View (≥ 1024px) */}
            <div className="hidden lg:flex justify-between items-center">
              <div className="flex items-center space-x-6">
                <div className="relative h-16 w-16">
                  {mentorDetails?.photoURL ? (
                    <img
                      src={mentorDetails.photoURL}
                      alt={discipleData?.name || 'Disciple'}
                      className="h-16 w-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                      <UserCircleIcon className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Discipleship Planning for {discipleData?.name?.split(/\s+/)[0] || '...'}</h1>
                  <p className="text-sm text-gray-600 mt-0.5">Your notebook to make content selections, take notes, and choose next questions for your Conversations.</p>
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <Link
                  to={`/view-disciple/${discipleId}/listen-connect-help`}
                  className="px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-md text-sm text-indigo-800 hover:bg-indigo-100 flex items-center space-x-2"
                >
                  <ChatBubbleBottomCenterTextIcon className="h-5 w-5" />
                  <span>Listen, Connect & Help</span>
                </Link>
                <Link
                  to={`/disciple-menu/${discipleId}`}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                >
                  <Squares2X2Icon className="h-5 w-5" />
                  <span>Menu</span>
                </Link>
                <Link
                  to={`/disciple-history/${discipleId}`}
                  state={{ discipleData, userAppID }}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  Message History
                </Link>
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-2 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area — four modules in 2x2 grid: Question Planning, Take Notes, Next Bible Verses, Content Track */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 1. Question Planning (light blue) */}
          <div className="rounded-xl shadow-sm border border-blue-100 bg-blue-50/70">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                Question Planning
              </h2>
              <p className="text-sm text-gray-600 mb-4">Choose a survey that you like to ask them good questions.</p>
              {!subscribedSurveyId ? (
                <>
                  {surveys.length === 0 ? (
                    <p className="text-gray-500 text-sm">No surveys available.</p>
                  ) : (
                    <>
                      <ul className="space-y-2">
                        {surveys.slice(0, SURVEYS_SHOWN_INLINE).map(s => (
                          <li key={s.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <span className="font-medium text-gray-900 text-sm">{s.name}</span>
                            <button
                              onClick={() => subscribeToSurvey(s.id)}
                              disabled={surveyUpdating}
                              className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                              Subscribe
                            </button>
                          </li>
                        ))}
                      </ul>
                      {surveys.length > SURVEYS_SHOWN_INLINE && (
                        <button
                          type="button"
                          onClick={() => setShowAllSurveysModal(true)}
                          className="mt-3 w-full py-2.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-200 transition"
                        >
                          More Survey Options ({surveys.length} total)
                        </button>
                      )}
                    </>
                  )}
                  <p className="mt-3 text-sm">
                    <Link to={`/view-disciple/${discipleId}/listen-connect-help`} className="text-indigo-600 hover:underline">Start Conversation</Link>
                    {' '}to send questions and add notes.
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-gray-600">Survey: <span className="font-medium text-gray-900">{survey?.name}</span></p>
                    <button
                      onClick={unsubscribeFromSurvey}
                      disabled={surveyUpdating}
                      className="text-sm text-gray-500 hover:text-red-600"
                    >
                      Unsubscribe
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">Next {Math.min(4, Math.max(0, surveyQuestions.length - currentQuestionIndex))} question(s):</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                    {surveyQuestions.slice(currentQuestionIndex, currentQuestionIndex + 4).map((q, i) => (
                      <li key={q.id || currentQuestionIndex + i}>{q.questionText?.slice(0, 80)}{(q.questionText?.length || 0) > 80 ? '…' : ''}</li>
                    ))}
                  </ul>
                  {surveyQuestions.length === 0 && <p className="text-gray-500 text-sm">No questions in this survey yet.</p>}
                  <p className="mt-3 text-sm">
                    <Link to={`/view-disciple/${discipleId}/listen-connect-help`} className="text-indigo-600 hover:underline">Send next question in Start Conversation</Link>
                  </p>
                </>
              )}
            </div>
          </div>

          {/* 2. Take Notes (light green) */}
          <div className="rounded-xl shadow-sm border border-green-200 bg-green-50/70">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">Take Notes</h2>
              <p className="text-sm text-gray-600 mb-4">Your notes help TOM to suggest verses that can be helpful to send them as a message or pray over them.</p>
              <MentoringNotesSection 
                discipleId={discipleId}
                userAppID={userAppID}
                onNoteAdded={handleNoteAdded}
              />
            </div>
          </div>

          {/* 3. Next Bible Verses (light yellow) */}
          <div className="rounded-xl shadow-sm border border-amber-200 bg-amber-50/70">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
                <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-amber-700" />
                Next Bible Verses
              </h2>
              <p className="text-sm text-gray-600 mb-4">Your notes help find references that you can use to pray, share, encourage and teach your disciples.</p>
              <div className="hidden">
                <Suggestions 
                  discipleId={discipleId}
                  allVerses={allVerses}
                  userAppID={userAppID}
                  notes={discipleNotes}
                  historicalVerses={allVerses}
                  sentVerses={sentVerses}
                  onSuggestionsGenerated={handleSuggestionsGenerated}
                  key={`suggestions-${noteUpdateTrigger}-${refreshTrigger}`}
                />
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Suggested next verses</h3>
                  <SuggestedVerses
                    suggestedNextVerses={suggestedNextVerses}
                    onVerseSelect={handleVerseSelect}
                    sentVerses={sentVerses}
                    showHeader={false}
                    key={`suggested-verses-${noteUpdateTrigger}-${refreshTrigger}`}
                  />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Search verses</h3>
                  <SearchVerses onVerseSelect={handleVerseSelect} />
                </div>
              </div>
            </div>
          </div>

          {/* 4. Content Track (light gray) */}
          <div className="rounded-xl shadow-sm border border-gray-200 bg-gray-100/80">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
                <BookOpenIcon className="h-5 w-5 text-gray-600" />
                Content Track
              </h2>
              <p className="text-sm text-gray-600 mb-4">Choose a content track that helps you share information from a wide variety of options.</p>
              {!subscribedContentTrackId ? (
                <>
                  {otherContentTracks.length > 0 && (
                    <ul className="space-y-2 mb-4">
                      {otherContentTracks.map(t => (
                        <li key={t.id} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                          <span className="font-medium text-gray-900 text-sm">{t.name}</span>
                          <button
                            type="button"
                            onClick={() => subscribeToContentTrack(t.id)}
                            disabled={contentTrackSubscribing != null}
                            className="px-3 py-1.5 bg-gray-700 text-white text-sm font-medium rounded-md hover:bg-gray-800 disabled:opacity-50"
                          >
                            {contentTrackSubscribing === t.id ? 'Subscribing…' : 'Subscribe'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link
                    to={`/browse-content-tracks/${discipleId}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700"
                  >
                    <BookOpenIcon className="h-4 w-4" />
                    More Track Options
                  </Link>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-gray-600">Track: <span className="font-medium text-gray-900">{contentTrack?.name || 'Content track'}</span></p>
                    <Link
                      to={`/browse-content-tracks/${discipleId}`}
                      className="text-sm text-gray-700 font-medium hover:underline"
                    >
                      More Track Options
                    </Link>
                  </div>
                  {contentTrackProgress && (
                    <p className="text-sm text-gray-500 mb-2">
                      {contentTrackProgress.sentCount ?? 0} of {contentTrackProgress.totalPublishedResources ?? 0} sent · Next 3:
                    </p>
                  )}
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                    {nextContentResources.map(r => (
                      <li key={r.id}>{r.title}</li>
                    ))}
                  </ul>
                  {nextContentResources.length === 0 && (contentTrackProgress?.sentCount || 0) > 0 && (
                    <p className="text-gray-500 text-sm">All resources in this track have been sent.</p>
                  )}
                  <p className="mt-3 text-sm">
                    <Link to={`/suggested-content/${discipleId}`} className="text-indigo-600 hover:underline">Send next resource</Link>
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Sent Verses */}
          <div className="lg:col-span-2">
            <SentVerses 
              discipleId={discipleId}
              userAppID={userAppID}
              onSentVersesUpdate={handleSentVersesUpdate}
              key={`sent-verses-${messageRefreshTrigger}`}
            />
          </div>
        </div>

        {/* Discipleship Progress (Missional Scale) */}
        <div className="mt-6">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Discipleship Progress</h2>
              <div className="max-w-3xl mx-auto">
                <MissionalScale 
                  discipleId={discipleId}
                  currentUser={{ appID: userAppID }}
                  initialValue={mentorDetails?.missionalScale || -5}
                  onChange={updateDiscipleMissionalScale}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* All Surveys Modal — choose from full list when there are many */}
      {showAllSurveysModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowAllSurveysModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Choose a survey</h3>
              <button
                type="button"
                onClick={() => setShowAllSurveysModal(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 flex-1">
              <p className="text-sm text-gray-600 mb-4">Select a survey to walk through with this disciple.</p>
              <ul className="space-y-2">
                {surveys.map(s => (
                  <li key={s.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="font-medium text-gray-900 text-sm">{s.name}</span>
                    <button
                      onClick={async () => {
                        await subscribeToSurvey(s.id);
                        setShowAllSurveysModal(false);
                      }}
                      disabled={surveyUpdating}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      Subscribe
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal - Remains centered across breakpoints */}
      {showSettings && (
        <DiscipleSettings
          discipleId={discipleId}
          currentUser={currentUser}
          initialData={discipleData}
          onClose={() => setShowSettings(false)}
          onUpdate={() => setRefreshTrigger(prev => prev + 1)}
        />
      )}
    </div>
  );
};

export default ViewDisciple;

